import { EntityType, useAppSelector } from '@frontend/common';
import { iconByType } from '@frontend/icons';
import { useLocale } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import React, { useEffect, useState } from 'react';
import { IconType } from 'react-icons';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ResolveState, resolveStore } from '../resolve/resolve-slice';

/**
 * @deprecated Platform click navigation (queryParamNavigation / adminOnClick) is replaced by
 * PlatformObjectBadge / openPlatformDetail. Resolve and display remain valid for existing badges.
 */
export interface BadgeProps<T> {
    className?: string;
    callback?: (object: T) => void;
    type: EntityType;
    mapDisplayName: (entity: T) => string | undefined;
    resolve: (id: string) => string;
    id?: string;
    icon?: IconType | null;
    overWriteIcon?: (entity: T) => IconType;
    navigateString?: (entity: T) => string;
    disableClick?: boolean;
    navigateOnDisplayName?: boolean;
    display?: string;
    parentIds?: Record<string, string>;
}

/**
 * @deprecated Platform click navigation (queryParamNavigation / adminOnClick) is replaced by
 * PlatformObjectBadge / openPlatformDetail. Resolve and display remain valid for existing badges.
 */
export function useBadgeRepository<T>(props: BadgeProps<T>) {
    const navigate = useNavigate();
    const location = useLocation();
    const [queryParams, setQueryParams] = useSearchParams();
    const { locale } = useLocale();
    const resolveState = useAppSelector(useSelector, resolveStore);
    const [object, changeObject] = useState<T | null>(null);
    const [requested, changeRequested] = useState<boolean>(false);
    const [disabled, changeDisabled] = useState<boolean>(true);
    const [displayName, changeDisplayName] = useState<React.ReactNode | undefined>(props.display);
    const [resolverKey, changeResolverKey] = useState<string | undefined>();
    const [resolverPending, changeResolverPending] = useState<boolean>(true);
    const { closeAllModals } = useModals();
    const isPlatform = process.env['NX_PUBLIC_APP'] === 'platform';

    useEffect(() => {
        //Bit of a weird construct but prevents not found from showing prematurely
        let interval: string | number | NodeJS.Timeout | undefined = undefined;
        if (resolverKey && !object) {
            const check = () => {
                const value = localStorage.getItem(resolverKey);
                if (value != null && JSON.parse(value!) === true) {
                    changeResolverPending(true);
                } else {
                    changeResolverPending(false);
                    clearInterval(interval);
                }
            };

            check();
            interval = setInterval(check, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [resolverKey]);

    useEffect(() => {
        if (props.display || props.display === '') {
            changeDisabled(false);
            return;
        }
        if (props.id != undefined) {
            const found = findEntity(resolveState, props.type, props.id);
            if (found) {
                changeDisplayName(props.mapDisplayName(found as T));
                changeObject(found as T);
                changeDisabled(false);
                props.callback && props.callback(found as T);
                if (!requested) {
                    //lazy load anyway to check for updates
                    changeRequested(true);
                    changeResolverKey(props.resolve(props.id));
                }
            } else if (requested == false) {
                changeRequested(true);
                changeResolverKey(props.resolve(props.id));
            }

            if (requested && !found && !resolverPending) {
                changeDisabled(true);
                changeDisplayName(
                    <FormattedMessage
                        id='badge.not_found'
                        defaultMessage='Not found'
                    />
                );
            }
        } else {
            changeDisabled(true);
            changeDisplayName(
                <FormattedMessage
                    id='badge.none'
                    defaultMessage='None'
                />
            );
        }
    }, [resolveState, props.id, locale, resolverPending]);

    useEffect(() => {
        if (props.id != undefined) {
            const found = findEntity(resolveState, props.type, props.id);
            if (!found && requested) {
                changeResolverKey(props.resolve(props.id));
                changeDisplayName(undefined);
            }
        }
    }, [props.id]);

    const adminOnClick = (e: any, s: string) => {
        e.stopPropagation();
        if (disabled) return;
        closeAllModals();
        navigate(`/admin${s}/detail`);
    };

    const onClick = (e: any) => {
        e.stopPropagation();
        if (disabled) return;
        const urlValue = props.type + '_id' + '-' + props.id;
        const newParams = new URLSearchParams(queryParams);
        const detail = newParams.get('details');
        if (detail) {
            newParams.set('details', JSON.stringify([...(JSON.parse(detail) as string[]), urlValue]));
        } else {
            newParams.set('details', JSON.stringify([urlValue]));
        }

        // Add parent IDs to query params with 'parent_' prefix
        if (props.parentIds) {
            Object.entries(props.parentIds).forEach(([key, value]) => {
                if (value) {
                    newParams.set(`parent_${key}`, value);
                }
            });
        }

        setQueryParams(newParams);
    };

    return {
        displayName,
        disabled: disabled || !isPlatform,
        object,
        onClick,
        icon: iconByType(props.type),
        isPlatform,
        adminOnClick
    };
}

const findEntity = (resolveState: ResolveState, type: EntityType, id: string) => {
    if (!resolveState.resolved) return null;
    if (!resolveState.resolved[type]) return null;
    if (!resolveState.resolved[type][id]) return null;
    return resolveState.resolved[type][id];
};
